// src/components/BankBranchSelect.js
import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, TextField, Stack } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchBanks, fetchBranches } from '../slices/bankDataSlice';

const MANUAL_OPTION = { id: '__manual__', manual: true };

const bankLabel = (bank) => bank?.name_ar || bank?.name || bank?.name_he || '';
const branchLabel = (branch) => branch?.name_ar || branch?.name || branch?.name_he || '';

const BankBranchSelect = ({
  fieldSx,
  bankId,
  bankName,
  branchId,
  branchName,
  onChange,
  disabled,
}) => {
  const dispatch = useDispatch();
  const { banks, banksLoaded, branchesByBank } = useSelector((state) => state.bankData);
  // Editing an existing cheque only has a name/number snapshot, no reference id —
  // start in manual mode so the saved name is visible.
  const [manualBank, setManualBank] = useState(() => !bankId && !!bankName);
  const [manualBranch, setManualBranch] = useState(() => !branchId && !!branchName);

  useEffect(() => {
    if (!banksLoaded) {
      dispatch(fetchBanks());
    }
  }, [banksLoaded, dispatch]);

  useEffect(() => {
    if (bankId && !branchesByBank[bankId]) {
      dispatch(fetchBranches(bankId));
    }
  }, [bankId, branchesByBank, dispatch]);

  const branches = bankId ? (branchesByBank[bankId] || []) : [];

  const selectedBank = useMemo(() => banks.find((b) => b.id === bankId) || null, [banks, bankId]);
  const selectedBranch = useMemo(() => branches.find((b) => b.id === branchId) || null, [branches, branchId]);

  return (
    <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
      <Stack sx={{ flex: 1, minWidth: 0 }}>
      {!manualBank ? (
        <Autocomplete
          options={[...banks, MANUAL_OPTION]}
          value={selectedBank}
          disabled={disabled}
          getOptionLabel={(option) => (option.manual ? 'لم أجد البنك، إدخال يدوي...' : bankLabel(option))}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(event, value) => {
            if (value?.manual) {
              setManualBank(true);
              setManualBranch(true);
              onChange({ bankId: null, bankName: '', bankNumber: '', branchId: null, branchName: '', branchNumber: '' });
              return;
            }
            setManualBranch(false);
            onChange({
              bankId: value?.id || null,
              bankName: bankLabel(value) || '',
              bankNumber: value?.code || '',
              countryCode: value?.country_code || '',
              branchId: null,
              branchName: '',
              branchNumber: '',
            });
          }}
          renderInput={(params) => (
            <TextField {...params} label="البنك" fullWidth sx={fieldSx} />
          )}
        />
      ) : (
        <TextField
          label="اسم البنك"
          value={bankName || ''}
          onChange={(e) => {
            const value = e.target.value;
            onChange({ bankName: value });
            if (!value) setManualBank(false);
          }}
          disabled={disabled}
          fullWidth
          sx={fieldSx}
        />
      )}
      </Stack>

      <Stack sx={{ flex: 1, minWidth: 0 }}>
      {!manualBranch ? (
        <Autocomplete
          options={[...branches, MANUAL_OPTION]}
          value={selectedBranch}
          disabled={disabled || !bankId}
          getOptionLabel={(option) => (option.manual ? 'لم أجد الفرع، إدخال يدوي...' : branchLabel(option))}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(event, value) => {
            if (value?.manual) {
              setManualBranch(true);
              onChange({ branchId: null, branchName: '', branchNumber: '' });
              return;
            }
            onChange({
              branchId: value?.id || null,
              branchName: branchLabel(value) || '',
              branchNumber: value?.code || '',
            });
          }}
          renderInput={(params) => (
            <TextField {...params} label="الفرع" fullWidth sx={fieldSx} />
          )}
        />
      ) : (
        <TextField
          label="اسم الفرع"
          value={branchName || ''}
          onChange={(e) => {
            const value = e.target.value;
            onChange({ branchName: value });
            if (!value && bankId) setManualBranch(false);
          }}
          disabled={disabled}
          fullWidth
          sx={fieldSx}
        />
      )}
      </Stack>
    </Stack>
  );
};

export default BankBranchSelect;
